import { Block, type Change, Text } from '@gitbook/slate';

import {
    BLOCK_HINT,
    BLOCK_PARAGRAPH,
    BLOCK_QUOTE,
    BLOCK_TABS_ITEM,
    LEAF_TEXT_BLOCKS,
} from '../types';

/**
 * Insert a paragraph after the current block if none, and focus it.
 */
export function moveToNextLeafBlock(change: Change, currentBlock?: Block) {
    moveToSiblingLeafBlock(change, 1, currentBlock);
}

/**
 * Insert a paragraph before the current block if none, and focus it.
 */
export function moveToPreviousLeafBlock(change: Change, currentBlock?: Block) {
    moveToSiblingLeafBlock(change, -1, currentBlock);
}

/**
 * Insert a paragraph before / after the current block if none, and focus it.
 */
function moveToSiblingLeafBlock(
    change: Change,
    move: 1 | -1,
    currentBlock = resolveCurrentBlock(change)
) {
    if (!currentBlock) {
        return;
    }

    const { document } = change.value;

    const parent = document.getParent(currentBlock.key);

    if (!parent) {
        return;
    }
    const currentBlockIndex = parent.getChildIndex(currentBlock.key);

    const siblingBlock = parent.nodes.get(currentBlockIndex + move) as Block | void;
    if (!siblingBlock || !LEAF_TEXT_BLOCKS.includes(siblingBlock.type)) {
        const newParagraph = Block.create({
            type: BLOCK_PARAGRAPH,
            nodes: [Text.create('')],
        });
        change
            .insertNodeByKey(
                parent.key,
                move > 0 ? currentBlockIndex + move : currentBlockIndex,
                newParagraph
            )
            .moveToRangeOf(newParagraph);
    } else {
        if (move > 0) {
            change.collapseToStartOf(siblingBlock);
        } else {
            change.collapseToEndOf(siblingBlock);
        }
    }
}

/**
 * Find the block to insert leaf block after.
 */
function resolveCurrentBlock(change: Change): Block | null {
    const { document, startBlock } = change.value;

    if (!startBlock) {
        throw new Error('No current block');
    }
    let currentBlock = startBlock;

    if (!currentBlock) {
        return null;
    }

    // As long as we aren't in a non-void block we crawl back up.
    while (currentBlock?.isVoid) {
        const parent = document.getParent(currentBlock.key);

        // If we hit the root, stop there.
        if (
            !parent ||
            parent.object === 'document' ||
            parent.object === 'fragment' ||
            (parent.object === 'block' && isContainerBlock(parent))
        ) {
            break;
        }

        if (parent.object === 'inline') {
            throw new Error('Unexpected inline parent');
        }

        currentBlock = parent;
    }

    return currentBlock;
}

/**
 * Returns true if the block is the block should be considered as one unit of content
 * and we don't want to escape it.
 *
 * Fragments (for expandable, etc) are already considered as containers in `resolveCurrentBlock`.
 */
function isContainerBlock(block: Block): boolean {
    return [BLOCK_TABS_ITEM, BLOCK_QUOTE, BLOCK_HINT].includes(block.type);
}
